import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { OperationalIcon } from "./operational-icon";
import "./global-select-search.css";

interface SelectSnapshot {
  readonly element: HTMLSelectElement;
  readonly label: string;
  readonly options: readonly { disabled: boolean; iconFallback: boolean; iconSrc: string | null; label: string; value: string }[];
}

function selectLabel(select: HTMLSelectElement): string {
  const explicit = select.getAttribute("aria-label");
  if (explicit) return explicit;
  const label = select.labels?.[0];
  if (!label) return "Select an option";
  const copy = label.cloneNode(true) as HTMLLabelElement;
  copy.querySelectorAll("input, select, textarea, button").forEach((control) => control.remove());
  return copy.textContent?.trim() || "Select an option";
}

/** Adds a consistent searchable picker to native single-value selects application-wide. */
export function GlobalSelectSearch(): ReactNode {
  const [active, setActive] = useState<SelectSnapshot | null>(null);
  const [query, setQuery] = useState("");
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({ visibility: "hidden" });
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const open = (select: HTMLSelectElement): void => {
      if (select.disabled || select.multiple || select.closest(".thw-searchable-select")) return;
      setActive({
        element: select,
        label: selectLabel(select),
        options: Array.from(select.options).map((option) => ({
          disabled: option.disabled,
          iconFallback: option.dataset.iconFallback === "role",
          iconSrc: option.dataset.iconSrc || null,
          label: option.textContent?.trim() ?? option.label,
          value: option.value,
        })),
      });
      setQuery("");
      setPopupStyle({ visibility: "hidden" });
    };

    const onPointerDown = (event: PointerEvent): void => {
      const select = (event.target as Element | null)?.closest("select");
      if (!(select instanceof HTMLSelectElement) || select.multiple || select.disabled || select.closest(".thw-searchable-select")) return;
      event.preventDefault();
      open(select);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      const select = event.target instanceof HTMLSelectElement ? event.target : null;
      if (!select || select.multiple || select.disabled || select.closest(".thw-searchable-select")) return;
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        open(select);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  useEffect(() => {
    if (active) requestAnimationFrame(() => searchRef.current?.focus());
  }, [active]);

  useLayoutEffect(() => {
    if (active === null) return;

    const positionPopup = (): void => {
      if (!active.element.isConnected) {
        setActive(null);
        return;
      }
      const rect = active.element.getBoundingClientRect();
      const viewportPadding = 8;
      const gap = 4;
      const desiredHeight = Math.min(320, 58 + Math.min(active.options.length, 6) * 42);
      const availableBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
      const availableAbove = rect.top - gap - viewportPadding;
      const placeAbove = availableBelow < desiredHeight && availableAbove > availableBelow;
      const availableHeight = Math.max(120, placeAbove ? availableAbove : availableBelow);
      const popupHeight = Math.min(desiredHeight, availableHeight);
      const width = Math.min(
        Math.max(rect.width, 280),
        window.innerWidth - viewportPadding * 2,
      );
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
      );
      setPopupStyle({
        bottom: placeAbove ? window.innerHeight - rect.top + gap : undefined,
        left,
        maxHeight: popupHeight,
        top: placeAbove ? undefined : rect.bottom + gap,
        visibility: "visible",
        width,
        "--thw-global-select-options-max-height": `${String(Math.max(72, popupHeight - 58))}px`,
      } as CSSProperties);
    };

    positionPopup();
    window.addEventListener("resize", positionPopup);
    window.addEventListener("scroll", positionPopup, true);
    return () => {
      window.removeEventListener("resize", positionPopup);
      window.removeEventListener("scroll", positionPopup, true);
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const closeOutside = (event: PointerEvent): void => {
      const target = event.target as Element | null;
      if (!target?.closest(".thw-global-select-search") && target !== active.element) setActive(null);
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () => document.removeEventListener("pointerdown", closeOutside, true);
  }, [active]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return needle === "" ? active?.options ?? [] : (active?.options ?? []).filter((option) => option.label.toLocaleLowerCase().includes(needle));
  }, [active, query]);

  if (!active) return null;
  const choose = (value: string): void => {
    active.element.value = value;
    active.element.dispatchEvent(new Event("change", { bubbles: true }));
    active.element.focus();
    setActive(null);
  };
  return createPortal(
    <div
      aria-label={active.label}
      className="thw-global-select-search"
      role="dialog"
      style={popupStyle}
    >
      <input
        ref={searchRef}
        aria-label={`Search ${active.label}`}
        className="thw-control thw-input"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Escape") { active.element.focus(); setActive(null); } }}
        placeholder="Search options…"
        type="search"
        value={query}
      />
      <div className="thw-global-select-search__options" role="listbox" aria-label={active.label}>
        {filtered.map((option) => (
          <button
            aria-selected={option.value === active.element.value}
            className="thw-global-select-search__option"
            disabled={option.disabled}
            key={`${option.value}-${option.label}`}
            onClick={() => choose(option.value)}
            role="option"
            type="button"
          >
            {option.iconSrc === null && !option.iconFallback ? null : <span className="thw-global-select-search__option-icon" aria-hidden="true">{option.iconSrc === null ? <OperationalIcon name="role" /> : <img alt="" src={option.iconSrc} />}</span>}
            <span>{option.label}</span>
          </button>
        ))}
        {filtered.length === 0 ? <p className="thw-global-select-search__empty" role="status">No matching options</p> : null}
      </div>
    </div>,
    document.body,
  );
}
